// ✅ src/pages/OrderManagement/ProjectFinalBillsDoc.jsx
// Modern React replacement for ProjectFinalBillsdoc.aspx
//
// APIs:
//  GET  http://nissi-india.com/api/projectFinalBillsDoc/view?contractId=1
//  POST http://nissi-india.com/api/projectFinalBillsDoc/upload   (multipart "file")
//  POST http://nissi-india.com/api/projectFinalBillsDoc/save     { contractId, loginUserId, files: [] }
//  GET  http://nissi-india.com/api/projectFinalBillsDoc/file/:name       (open/view)
//  GET  http://nissi-india.com/api/projectFinalBillsDoc/download/:name   (download)

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaBars,
  FaHome,
  FaUpload,
  FaTrash,
  FaEye,
  FaDownload,
  FaSave,
  FaArrowLeft,
} from "react-icons/fa";

const API = "http://nissi-india.com/api/projectFinalBillsDoc";

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const safeName = (s) => String(s || "").trim();

export default function ProjectFinalBillsDoc() {
  const navigate = useNavigate();
  const location = useLocation();

  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const contractId = useMemo(
    () => toNum(qs.get("contractId") || qs.get("contractid") || 0),
    [qs]
  );

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );
  const loginUserId = useMemo(() => toInt(userIdLS, 0), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId || "",
      "x-finyear": finYear || "",
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  const [isDark, setIsDark] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  // list = filenames (already uploaded in server folder)
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);

  const openFile = (name) => {
    const n = safeName(name);
    if (!n) return;
    window.open(`${API}/file/${encodeURIComponent(n)}`, "_blank");
  };

  const downloadFile = (name) => {
    const n = safeName(name);
    if (!n) return;
    window.open(`${API}/download/${encodeURIComponent(n)}`, "_blank");
  };

  const loadView = async () => {
    if (!contractId) {
      toast.error("contractId missing in URL");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/view?contractId=${contractId}`, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "View failed");
        return;
      }

      setFiles(res.data?.data?.files || []);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, commonHeaders]);

  const uploadSingle = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("contractId", contractId);
    fd.append("loginUserId", loginUserId);

    const res = await axios.post(`${API}/upload`, fd, {
      headers: {
        ...commonHeaders,
        "Content-Type": "multipart/form-data",
      },
    });

    if (!res.data?.success) {
      throw new Error(res.data?.message || "Upload failed");
    }

    return res.data?.data?.filename;
  };

  const onPickFiles = async (ev) => {
    const picked = Array.from(ev.target.files || []);
    ev.target.value = "";

    if (!picked.length) return;

    setLoading(true);
    try {
      const uploadedNames = [];

      for (const f of picked) {
        const name = await uploadSingle(f);
        uploadedNames.push(name);
      }

      setFiles((prev) => Array.from(new Set([...prev, ...uploadedNames])));
      toast.success("File(s) added");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Upload error");
    } finally {
      setLoading(false);
    }
  };

  const removeLocal = (name) => {
    setFiles((prev) => prev.filter((x) => x !== name));
  };

  const handleSave = async () => {
    if (!contractId) {
      toast.error("contractId missing in URL");
      return;
    }

    if (!loginUserId) {
      toast.error("loginUserId missing in localStorage");
      return;
    }

    if ((files || []).length === 0) {
      toast.error("Please upload at least one document");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        contractId,
        loginUserId,
        files,
      };

      const res = await axios.post(`${API}/save`, payload, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Save failed");
        return;
      }

      toast.success(res.data?.message || "Saved Successfully");
      await loadView();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen transition-all ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
      <ToastContainer position="top-right" autoClose={2500} />

      {/* Top bar */}
      <div
        className={`sticky top-0 z-50 border-b shadow-lg ${
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        }`}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen((p) => !p)}
                className={`p-2 rounded-lg ${
                  isDark ? "hover:bg-gray-700 text-gray-200" : "hover:bg-gray-100 text-gray-700"
                }`}
              >
                <FaBars />
              </button>

              <div className="flex items-center gap-2">
                <FaHome className="text-blue-600" />
                <div className={`font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Project Management
                </div>
              </div>

              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Contract ID: <b>{contractId || "-"}</b>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsDark((p) => !p)}
                className={`px-3 py-2 rounded-lg ${
                  isDark ? "bg-yellow-600 text-white" : "bg-gray-800 text-white"
                }`}
              >
                {isDark ? "☀️" : "🌙"}
              </button>

              <button
                onClick={() => navigate(-1)}
                className="px-3 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white flex items-center gap-2"
              >
                <FaArrowLeft /> Back
              </button>

              <button
                disabled={loading}
                onClick={handleSave}
                className={`px-4 py-2 rounded-lg text-white flex items-center gap-2 ${
                  loading ? "bg-green-600/60 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
                }`}
              >
                <FaSave /> Save
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar placeholder */}
        {sidebarOpen && <div className="w-0 md:w-0" />}

        {/* Main */}
        <div className="flex-1">
          <div
            className={`p-6 transition-all ${
              isDark
                ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900"
                : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
            }`}
          >
            {/* Header */}
            <div
              className={`rounded-2xl border shadow-xl p-6 mb-6 ${
                isDark ? "bg-gray-900/60 border-gray-700" : "bg-white/80 border-gray-200"
              }`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                    Project Final Bills Document
                  </div>
                  <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Upload, preview, download and save documents.
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    onChange={onPickFiles}
                    className="hidden"
                  />
                  <button
                    disabled={loading}
                    onClick={() => fileRef.current?.click()}
                    className={`px-4 py-2 rounded-lg text-white flex items-center gap-2 ${
                      loading ? "bg-blue-600/60 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    <FaUpload /> Add / Upload
                  </button>
                </div>
              </div>
            </div>

            {/* File list */}
            <div
              className={`rounded-2xl border shadow-xl ${
                isDark ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-200"
              }`}
            >
              <div
                className="px-6 py-4 border-b flex items-center justify-between gap-3"
                style={{ borderColor: isDark ? "#374151" : "#E5E7EB" }}
              >
                <div className={`font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Uploaded Documents
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Count: <b>{files.length}</b>
                </div>
              </div>

              <div className="p-6">
                {files.length === 0 ? (
                  <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    No documents yet. Click <b>Add / Upload</b>.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {files.map((f, idx) => (
                      <div
                        key={`${f}-${idx}`}
                        className={`p-4 rounded-xl border flex flex-col md:flex-row md:items-center md:justify-between gap-3 ${
                          isDark ? "bg-gray-900/40 border-gray-700" : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div className="min-w-0">
                          <div
                            className={`font-semibold truncate ${
                              isDark ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {f}
                          </div>
                          <div className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                            Stored in: uploads/ProjectFinalBills/
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => openFile(f)}
                            className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2"
                          >
                            <FaEye /> View
                          </button>

                          <button
                            onClick={() => downloadFile(f)}
                            className="px-3 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-2"
                          >
                            <FaDownload /> Download
                          </button>

                          <button
                            onClick={() => removeLocal(f)}
                            className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
                          >
                            <FaTrash /> Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-6 flex flex-wrap gap-2">
                  <button
                    disabled={loading}
                    onClick={handleSave}
                    className={`px-5 py-3 rounded-lg text-white flex items-center gap-2 ${
                      loading ? "bg-green-600/60 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
                    }`}
                  >
                    <FaSave /> Save Documents
                  </button>

                  <button
                    disabled={loading}
                    onClick={loadView}
                    className={`px-5 py-3 rounded-lg text-white flex items-center gap-2 ${
                      loading ? "bg-gray-600/60 cursor-not-allowed" : "bg-gray-600 hover:bg-gray-700"
                    }`}
                  >
                    Refresh
                  </button>
                </div>

                {loading ? (
                  <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Working...
                  </div>
                ) : null}
              </div>
            </div>

            <div className="h-10" />
          </div>
        </div>
      </div>
    </div>
  );
}